import React from 'react'
import { Link } from 'react-router-dom'
import { Download } from 'lucide-react'
import moment from 'moment'

import { ROUTES } from 'config/routes'

// Button は JS コンポーネント（レガシー）だが再利用指定あり
import ButtonJS from 'components/Button'
import PaginationJS from 'containers/HomePage/components/Pagination'
import DayPickerInputJS from 'containers/HomePage/components/DayPickerInput'
import { PaginationSummary } from 'components/PaginationSummary'

import type { DeliveryPerformanceFilter } from '../type'
import styles from './PerformanceFilterBar.module.css'

// JS コンポーネントの型キャスト（レガシー JS から TS で安全に呼び出すためのラッパー型）
// unknown 経由でキャストすることで TS の型不一致エラーを回避する
const Button = ButtonJS as unknown as React.ComponentType<{
  color?: string
  className?: string
  onClick?: () => void
  children?: React.ReactNode
}>
const Pagination = PaginationJS as unknown as React.ComponentType<{
  meta: { total: number; current: number }
  onNavigateRequest: (page: number) => void
  size?: string
}>
const DayPickerInput = DayPickerInputJS as unknown as React.ComponentType<{
  value: Date
  onDayChange: (val: Date) => void
  name?: string
  settingHoliday?: string[]
}>

type Props = {
  filter: DeliveryPerformanceFilter
  total: number
  totalPage: number
  currentPage: number
  perPage: number
  onFilterChange: (patch: Partial<DeliveryPerformanceFilter>) => void
  onSearch: () => void
  onPageChange: (page: number) => void
}

export const PerformanceFilterBar: React.FC<Props> = ({
  filter,
  total,
  totalPage,
  currentPage,
  perPage,
  onFilterChange,
  onSearch,
  onPageChange,
}) => {
  const handleDateFromChange = (val: Date) => {
    const dateFrom = moment(val).format('YYYY-MM-DD')
    const dateTo = filter.dateTo
    // from > to になる場合は to も合わせてクランプ
    if (dateFrom > dateTo) {
      onFilterChange({ dateFrom, dateTo: dateFrom })
    } else {
      onFilterChange({ dateFrom })
    }
  }

  const handleDateToChange = (val: Date) => {
    const dateTo = moment(val).format('YYYY-MM-DD')
    const dateFrom = filter.dateFrom
    // to < from になる場合は from も合わせてクランプ
    if (dateTo < dateFrom) {
      onFilterChange({ dateFrom: dateTo, dateTo })
    } else {
      onFilterChange({ dateTo })
    }
  }

  return (
    <div className={styles.filterBar}>
      {/* 配送開始日時範囲 */}
      <div className={`${styles.filterGroup} ${styles.dateRange}`}>
        <DayPickerInput
          name="dateFrom"
          value={moment(filter.dateFrom).toDate()}
          onDayChange={handleDateFromChange}
          settingHoliday={[]}
        />
        <span className={styles.rangeSeparator}>〜</span>
        <DayPickerInput
          name="dateTo"
          value={moment(filter.dateTo).toDate()}
          onDayChange={handleDateToChange}
          settingHoliday={[]}
        />
      </div>

      {/* ドライバー電話番号 */}

      <div className={styles.filterGroup}>
        <input
          type="tel"
          className={styles.phoneInput}
          placeholder="090-0000-0000"
          value={filter.driverPhone}
          onChange={(e) => onFilterChange({ driverPhone: e.target.value })}
          aria-label="ドライバー電話番号"
        />
      </div>

      <div className={styles.searchButtonWrapper}>
        <Button className={styles.searchButton} color="blue" onClick={onSearch}>
          検索
        </Button>
        <Link
          className={styles.csvDownloadLink}
          to={{
            pathname: ROUTES.deliveryPerformanceCsvDownload,
            // 現在の絞り込み期間を DL 画面の初期値として引き継ぐ（電話番号は CSV 対象外）
            state: { dateFrom: filter.dateFrom, dateTo: filter.dateTo },
          }}
        >
          <Download size={16} aria-hidden={true} />
          CSV実績ダウンロード
        </Link>
      </div>

      {/* ページング: 件数表示 + ページネーション */}
      {total > 0 && (
        <div className={styles.paginationArea}>
          <PaginationSummary totalCount={total} page={currentPage} perPage={perPage} />
          <Pagination
            meta={{ total: totalPage, current: currentPage }}
            onNavigateRequest={onPageChange}
          />
        </div>
      )}
    </div>
  )
}
